import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { AiFillEdit, AiOutlineClose } from "react-icons/ai";
import { ImCross } from "react-icons/im";
import ReactModal from "react-modal";
import ReactQuill from "react-quill";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import { readVideoRequest } from "../../store/VideoResouce/videoResourcesActions";

import LensSearch from "../../assets/icons/LensSearch.svg";
import close from "../../assets/icons/close.svg";

import DropdownField from "../InputFields/DropDown";
import UploadControlsV2 from "../TWG/UploadControlsV2";
import GalleryPickerField from "../../components/ImageGallery/GalleryPickerField";

import {
  saveVideoRequest,
  updateVideoRequest,
  deleteVideoRequest,
} from "../../store/VideoResouce/videoResourcesActions";

import { readGalleryRequest } from "../../store/Gallery/galleryAction";
import { setActiveLanguage } from "../../store/Dashboard/DashboardAction";

const GalleryLibraryModal = ({ isOpen, onClose, handleGallerySelect, church }) => {
  const dispatch = useDispatch();
  const { languagesList, activeLanguage } = useSelector((state) => state.language);
  const { videos, loading } = useSelector((state) => state.videoResource);

  const [videoList, setVideoList] = useState([]);
  const [galleryResetKey, setGalleryResetKey] = useState(Date.now());

  // Form fields for add / edit
  const [videoName, setVideoName] = useState("");
  const [videoURL, setVideoURL] = useState("");
  const [videoDescription, setVideoDescription] = useState("");
  const [thumbnailImage, setThumbnailImage] = useState(null);

  const [editingIndex, setEditingIndex] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchText, setSearchText] = useState("");

  const ministriesObjId = church?._id;

  // ----------------------------
  // Load videos from Redux state
  // ----------------------------
  useEffect(() => {
    if (ministriesObjId && isOpen) {
      dispatch(readVideoRequest({ ministriesObjId }));
    }
  }, [ministriesObjId, isOpen, dispatch]);

  // Sync Redux videos with local state
    useEffect(() => {
    if (videos) {
      setVideoList(videos);
    }
  }, [videos]);

  // ----------------------------
  // Save / Update handler
  // ----------------------------
  const resetForm = () => {
    setEditingIndex(null);
    setVideoName("");
    setVideoURL("");
    setVideoDescription("");
    setThumbnailImage(null);
    setGalleryResetKey(Date.now());
    setIsAdding(false);
  };

  const handleSave = () => {
    if (!videoName) return toast.error("Video name required!");
    if (!videoURL) return toast.error("Video URL required!");
    if (!ministriesObjId) return toast.error("Church ID missing!");

    const payload = {
      ministriesObjId,
      videoName,
      videoDescription,
      tumbnailGalleryObjId: thumbnailImage || null,
      youtubeurl: videoURL,
    };

    // Update
    if (editingIndex !== null) {
      const id = videoList[editingIndex]?._id;
      if (!id) return toast.error("Invalid video id for update");

      dispatch(
        updateVideoRequest(
          { _id: id, ...payload },
          (res) => {
            if (res?.type === 1) {
              toast.success("Video updated!");
              resetForm();
            } else {
              toast.error(res?.error || "Failed to update video");
            }
          }
        )
      );
      return;
    }

    // Save new
    dispatch(
      saveVideoRequest(
        payload,
        (res) => {
          if (res?.type === 1) {
            toast.success("Video saved!");
            resetForm();
          } else {
            toast.error(res?.error || "Failed to save video");
          }
        }
      )
    );
  };

  // ----------------------------
  // Delete
  // ----------------------------
  const handleDelete = (index) => {
    const id = videoList[index]?._id;
    if (!id) return toast.error("Invalid video id for delete");

    dispatch(
      deleteVideoRequest(
        { _id: id, ministriesObjId },
        (res) => {
          if (res?.type === 1) {
            toast.success("Video deleted!");
            if (editingIndex === index) resetForm();
          } else {
            toast.error(res?.error || "Failed to delete video");
          }
        }
      )
    );
  };

  // ----------------------------
  // Edit fill
  // ----------------------------
  const handleEdit = (item, index) => {
    setEditingIndex(index);
    setVideoName(item.videoName || "");
    setVideoURL(item.youtubeurl || "");
    setVideoDescription(item.videoDescription || "");
    setThumbnailImage(item.tumbnailGalleryObjId || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsAdding(true);
  };

  // ----------------------------
  // Thumbnail picker callback
  // ----------------------------
  const handleThumbSelect = (img) => {
    try {
      const candidate = img?.target?.value?.[0]?.id ?? img?.target?.value?.[0];
      setThumbnailImage(candidate || img || null);
    } catch {
      setThumbnailImage(img || null);
    }
  };

  // ----------------------------
  // Search filter
  // ----------------------------
  const filteredList = (videoList || []).filter((v) =>
    (v.videoName || "").toLowerCase().includes(searchText.toLowerCase())
  );

  // ----------------------------
  // UI Render
  // ----------------------------
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-70 flex justify-center items-start py-10 overflow-auto">
      <div className="bg-[#1b1b1b] w-[95%] max-w-6xl rounded-xl overflow-hidden min-h-[60vh] relative my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-4 border-b border-gray-800">
          <p className="text-white text-lg font-semibold">Video Manager</p>

          <div className="flex items-center gap-3">
            <div className="relative flex gap-3 px-4 py-2 font-[450] rounded-[12px] bg-[#131313] w-64 border border-gray-400 border-opacity-5">
              <img src={LensSearch} alt="search" />
              <input
                className="text-white placeholder-white placeholder-opacity-20 text-[15px] bg-transparent w-full focus:outline-none"
                value={searchText}
                placeholder="Search videos"
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <DropdownField
              value={activeLanguage}
              options={languagesList?.map((d) => ({
                value: d?.language_code,
                shortCode: d?.language_code,
                roleName: d?.language_name,
              }))}
              labelText=""
              width="w-44 mt-0"
              handleInputChange={(e) => dispatch(setActiveLanguage(e.target.value))}
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setIsAdding((p) => !p);
                if (isAdding) resetForm();
              }}
              className="bg-yellowish text-black px-4 py-2 rounded-md text-sm font-semibold hover:bg-yellow-400 transition"
            >
              {isAdding ? "Cancel" : "Add New"}
            </button>

            <img
              className="cursor-pointer"
              onClick={() => {
                resetForm();
                onClose();
              }}
              src={close}
              alt="close"
            />
          </div>
        </div>

        {/* Form area (Add / Edit) */}
        {isAdding && (
          <div className="px-8 py-6 border-b border-gray-800">
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="text"
                placeholder="Video Name"
                value={videoName}
                onChange={(e) => setVideoName(e.target.value)}
                className="bg-blackish p-2 w-full md:w-1/2 text-white text-[14px] rounded-md h-10"
              />

              <input
                type="text"
                placeholder="YouTube or Video URL"
                value={videoURL}
                onChange={(e) => setVideoURL(e.target.value)}
                className="bg-blackish p-2 w-full md:w-1/2 text-white text-[14px] rounded-md h-10"
              />
            </div>

            <div className="mt-3">
              <ReactQuill
                theme="snow"
                value={videoDescription}
                onChange={setVideoDescription}
                className="bg-blackish text-white rounded-md"
                placeholder="Enter Video Description"
              />
            </div>

            <div className="mt-3">
              <GalleryPickerField
                key={galleryResetKey}
                label="Thumbnail Image"
                name="thumbnail"
                church={church}
                handleChange={handleThumbSelect}
              />
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={handleSave}
                className="text-yellowish border border-yellowish rounded-xl py-2 px-6 text-[12px] font-medium"
              >
                {editingIndex !== null ? "Update Video" : "Save Video"}
              </button>
            </div>
          </div>
        )}

        {/* Grid list */}
        <div className="p-8 max-h-[60vh] overflow-y-auto custom-scroll">
          {loading ? (
            <div className="flex justify-center py-10 text-greyish">Loading videos...</div>
          ) : filteredList?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {filteredList.map((vid, index) => {
                const thumb = vid?.gallery?.imageUrl || "https://placehold.co/600x400?text=Video+Thumbnail";
                return (
                  <div key={vid._id} className="relative group bg-[#0f0f0f] rounded-lg overflow-hidden">
                    <div
                      className="w-full h-40 cursor-pointer"
                      onClick={() => setPreviewItem({ ...vid })}
                    >
                      <img src={thumb} alt={vid.videoName} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M6.271 11.243 10.271 8 6.271 4.757z" />
                        </svg>
                      </div>
                    </div>

                    <div className="mt-2 text-[13px] text-white text-center px-2 line-clamp-2">
                      {vid.videoName}
                    </div>

                    <div className="absolute top-2 left-2 flex gap-2">
                      <div className="bg-black/50 p-1 rounded-full cursor-pointer" onClick={() => handleEdit(vid, index)}>
                        <AiFillEdit className="text-white w-4 h-4" />
                      </div>
                      <div className="bg-black/50 p-1 rounded-full cursor-pointer" onClick={() => handleDelete(index)}>
                        <AiOutlineClose className="text-white w-4 h-4" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-greyish text-center py-10">
              {videoList.length === 0 ? "No videos found in gallery." : "No videos match your search."}
            </p>
          )}
        </div>

        {/* Preview modal for playing video */}
        <ReactModal
          isOpen={!!previewItem}
          onRequestClose={() => setPreviewItem(null)}
          style={{
            overlay: {
              zIndex: 999999,
              backgroundColor: "rgba(0,0,0,0.85)",
            },
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              transform: "translate(-50%, -50%)",
              background: "transparent",
              border: "none",
              padding: 0,
              overflow: "visible",
              zIndex: 9999999,
            },
          }}
        >
          {previewItem && (
            <div className="relative p-4">
              <div className="absolute top-2 right-2 cursor-pointer z-50" onClick={() => setPreviewItem(null)}>
                <ImCross className="text-white w-6 h-6" />
              </div>

              <div className="">
                <ReactPlayer
                  url={previewItem?.youtubeurl}
                  controls
                  playing
                  width="100%"
                  height="500px"
                />
              </div>

              <div className="flex justify-center mt-6 gap-3">
                <button
                  onClick={() => {
                    handleGallerySelect(previewItem);
                    setPreviewItem(null);
                    onClose();
                  }}
                  className="bg-yellowish text-black px-5 py-2 rounded-md font-semibold hover:bg-yellow-400"
                >
                  Select This Video
                </button>

                <button
                  onClick={() => setPreviewItem(null)}
                  className="text-white px-5 py-2 rounded-md border border-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </ReactModal>
      </div>
    </div>,
    document.getElementById("modal-root")
  );
};

export default GalleryLibraryModal;